import type { PluginListingEntry } from '@crowdex/agent-contracts/api';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { listApps } from '@/features/apps/apps-client';
import { nativePluginClient } from '@/lib/plugins/plugin-native-client';
import { buildPluginWorkbench, EMPTY_WORKBENCH, type PluginWorkbench } from './plugin-workbench';

export const PluginWorkbenchContext = createContext<PluginWorkbench>(EMPTY_WORKBENCH);
export const useWorkbench = () => useContext(PluginWorkbenchContext);

export function usePluginWorkbench(workspace: string | null, enabled = true): PluginWorkbench {
  const [snapshot, setSnapshot] = useState<{
    workspace: string | null;
    apps: PluginListingEntry[];
    revision: number;
    error?: string;
  }>({ workspace, apps: [], revision: 0 });
  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let sequence = 0;
    let previousApps = '';
    let previousPlugins = '';
    let refreshing = false;
    let refreshAgain = false;
    async function refresh() {
      if (refreshing) {
        refreshAgain = true;
        return;
      }
      refreshing = true;
      const request = ++sequence;
      try {
        const [apps, pluginsResult] = await Promise.all([
          listApps(),
          // The workbench is built from apps only; the plugins are watched so that their
          // consumers (skills, MCP servers) follow changes made elsewhere.
          nativePluginClient.list(workspace ?? undefined).then(
            (plugins) => ({ plugins }),
            () => ({ plugins: undefined }),
          ),
        ]);
        if (disposed || request !== sequence) return;
        if (pluginsResult.plugins !== undefined) {
          const pluginsDigest = JSON.stringify(
            pluginsResult.plugins.map((plugin) => ({
              name: plugin.name,
              enabled: plugin.enabled,
              version: plugin.version,
              installedAt: plugin.installedAt,
              contentDigest: plugin.contentDigest,
              mcpServers: plugin.mcpServers,
              source: plugin.source,
              skills: plugin.skills,
              displayName: plugin.displayName,
            })),
          );
          if (previousPlugins !== '' && pluginsDigest !== previousPlugins)
            window.dispatchEvent(new Event('crowdex-plugin-inventory-updated'));
          previousPlugins = pluginsDigest;
        }
        const digest = JSON.stringify(apps);
        if (digest === previousApps) return;
        previousApps = digest;
        setSnapshot((previous) => ({
          workspace,
          apps,
          revision: previous.revision + 1,
        }));
      } catch (error) {
        previousApps = '';
        if (!disposed && request === sequence)
          setSnapshot((previous) => ({
            workspace,
            apps: [],
            revision: previous.revision + 1,
            error: error instanceof Error ? error.message : String(error),
          }));
      } finally {
        refreshing = false;
        if (refreshAgain && !disposed) {
          refreshAgain = false;
          void refresh();
        }
      }
    }
    void refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 10000);
    window.addEventListener('crowdex-plugins-changed', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      window.removeEventListener('crowdex-plugins-changed', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [workspace, enabled]);
  return useMemo(() => {
    if (!enabled) return EMPTY_WORKBENCH;
    if (snapshot.workspace !== workspace || snapshot.revision === 0) return EMPTY_WORKBENCH;
    const result = buildPluginWorkbench(
      { workspaceMatches: new Set(), capabilities: new Set() },
      snapshot.revision,
      snapshot.apps,
    );
    if (snapshot.error) result.errors.push(snapshot.error);
    return result;
  }, [snapshot, workspace, enabled]);
}
